import {
  Map as IMap,
  Set as ISet,
} from 'immutable';
import {
  Component,
  createMemo,
  createResource,
  For,
  JSXElement,
  Show,
  useContext,
} from 'solid-js';
import {
  ActionDropdown,
  allTags,
  AppPage,
  AuthContext,
  Button,
  CredentialProviderLabel,
  FaIcon,
  FormatDate,
  Link,
  LocalizationContext,
  MarkdownView,
  PrettyDataSize,
  ReportLink,
  ResourceFallback,
  Routes,
  Tags,
  useRoutingNavigate,
  WebPlayer,
} from '../../components';
import {
  StoreBranchInfo,
  StorePackageInfo,
  StoreProjectInfo,
} from '../../components/api-generated';
import config from '../../config';
import { showErrorScope } from '../../dialogs/basic';
import {
  linkStore,
  linkStoreProject,
  linkStoreProjectBranchPackagePlay,
} from './links';

export const RouteStore: Component = () => {
  const { status: authStatus, api, tokenlessApi } = useContext(AuthContext)!;
  const { t } = useContext(LocalizationContext)!;

  return <>
    <AppPage
      title={t('store')}
      breadcrumb={() => <Link href={linkStore}>{t('store')}</Link>}
      uiClass="store"
    />
    <Routes routes={[
      {
        path: '',
        component: () => <>
          <h1>{t('store')}</h1>
          <div class="highlight warning"><p>{t('in_development')}</p></div>
          <div class="footer">
          </div>
        </>,
      },
      {
        component: (props) => {
          const [project] = createResource(async () => {
            try {
              return await tokenlessApi.getStoreProjects({
                project: props.route,
              });
            } catch(e) {
              return undefined;
            }
          });
          const branches = createMemo(() => IMap(project()?.branches?.map?.((branch) => [branch.id, branch])));
          return <Show when={project()} keyed fallback={<ResourceFallback resource={project} page />}>{(project) => <>
            <AppPage
              title={project.title}
              breadcrumb={() => <Link href={linkStoreProject(project.id)}>{project.title}</Link>}
              uiClass="store_project"
            />
            <Routes routes={[
              {
                path: '',
                component: () => {
                  const [starred, { refetch: refetchStarred }] = createResource(() => authStatus() === 'authenticated' ? project : undefined, async (project) => {
                    return await api.getProjectsStar({
                      project: project.id,
                    });
                  });

                  const onProjectStar = async () => {
                    await api.putProjectsStar({
                      project: project.id,
                      requestBody: !starred(),
                    });
                    refetchStarred();
                  };

                  return <>
                    <h1>
                      <span class="user-selectable">{project.title}</span>
                      <div class="hint">{t('store.project.hint')}</div>
                    </h1>
                    <Show when={project.owner.isThirdParty}>
                      <div class="highlight warning">
                        <p>{t('store.project.content.warning')}</p>
                      </div>
                    </Show>
                    <h2>{t('store.project.owner')}</h2>
                    <div class="list owner">
                      <div>
                        <div>
                          <div class="owner">{project.owner.title}</div>
                          <For each={project.owner.credentials}>{(credential) => <>
                            <Show when={credential.profileUrl} fallback={<div class="credential"><CredentialProviderLabel provider={credential.provider} /></div>}>
                              <a class="credential" href={credential.profileUrl} rel="ugc nofollow" target="_blank"><CredentialProviderLabel provider={credential.provider} /></a>
                            </Show>
                          </>}</For>
                        </div>
                      </div>
                    </div>
                    <h2>{t('store.project.description')}</h2>
                    <div class="description">
                      <MarkdownView markdown={() => project.description} ugc={true} level={2} linkInNewTab={true} />
                    </div>
                    <h2>{t('store.project.content')}</h2>
                    <For each={project.branches}>{(branch) => <>
                      <h3 class="user-selectable">{t('store.project.branch_version')(<strong>{branch.title}</strong>, <strong>{branch.versionTitle}</strong>)}</h3>
                      <MarkdownView markdown={() => ({
                        markdown: branch.versionDescription,
                        lang: project.description.lang,
                        images: [],
                      })} level={3} ugc linkInNewTab />
                      <div class="list packages">
                        <For each={branch.packages}>{(pkg) => <StorePackage project={project} branch={branch} pkg={pkg} />}</For>
                      </div>
                      <p>{t('store.project.branch_version.published')(<FormatDate date={new Date(branch.branchVersionPublished)} />)}</p>
                    </>}</For>
                    <div class="footer">
                      <div class="buttons">
                        <Show when={starred() != null}>
                          <Button light onClick={onProjectStar}>{
                            starred()
                              ? <><FaIcon star solid inline weak />{t('store.project.starred')}</>
                              : <><FaIcon star regular inline weak />{t('store.project.star')}</>
                            }</Button>
                        </Show>
                        <div class="spacer" />
                        <ReportLink
                          title={<><FaIcon regular flag inline weak />{t('report_content')}</>}
                          subject={`Report store project ${project.id ?? ''}`}
                          body={
`Re: store project ${project.id ?? ''}
Link: ${window.location.href}
Title: ${project.title ?? ''}
`}
                        />
                      </div>
                    </div>
                  </>;
                },
              },
              {
                component: (props) => {
                  const branch = createMemo(() => branches().get(props.route));
                  const packages = createMemo(() => IMap(branch()?.packages?.map?.((pkg) => [pkg.id, pkg])));
                  return <Show when={branch()} keyed>{(branch) => <>
                    <AppPage
                      title={branch.title}
                      breadcrumb={() => branch.title}
                      uiClass="store_branch"
                    />
                    <Routes routes={[
                      {
                        component: (props) => {
                          const pkg = createMemo(() => packages().get(props.route));
                          return <Show when={pkg()} keyed>{(pkg) => <>
                            <AppPage
                              title={pkg.title}
                              breadcrumb={() => pkg.title}
                              uiClass="store_package"
                            />
                            <Routes routes={[
                              {
                                path: 'play',
                                component: () => {
                                  const [spaceUrlResource] = createResource(async () => {
                                    const spaceUrl = new URL(await tokenlessApi.getStoreProjectsBranchesPackagesSpace({
                                      project: project.id,
                                      branch: branch.id,
                                      _package: pkg.id,
                                    }));
                                    spaceUrl.pathname += pkg.entrypoint.join('/');
                                    return spaceUrl.toString();
                                  });
                                  return <WebPlayer spaceUrlResource={spaceUrlResource} />;
                                },
                              },
                            ]} />
                          </>}</Show>;
                        },
                      },
                    ]} />
                  </>}</Show>;
                },
              },
            ]} />
          </>}</Show>;
        },
      },
    ]} />
  </>;
};

const StorePackage: Component<{
  project: StoreProjectInfo;
  branch: StoreBranchInfo;
  pkg: StorePackageInfo;
}> = (props) => {
  const { tokenlessApi } = useContext(AuthContext)!;
  const { t } = useContext(LocalizationContext)!;
  const navigate = useRoutingNavigate();

  type TagOsOption = keyof (typeof allTags)['os']["options"];
  type TagInfo = {
    os?: TagOsOption;
  };
  const tagsInfo = createMemo<TagInfo>(() => {
    const osTags: TagOsOption[] = [];
    const info: TagInfo = {};
    for(const tag of props.pkg.tags) {
      const [tagType, tagValue] = tag.split(':');
      switch(tagType) {
      case 'os':
        osTags.push(tagValue as TagOsOption);
        break;
      }
    }
    if(osTags.length == 1) {
      info.os = osTags[0];
    }
    return info;
  });

  type Action = {
    title: string;
    icon?: JSXElement;
    onAction: () => void;
  };

  const actions = createMemo<Action[]>(() => {
    const actions: Action[] = [];

    if(props.pkg.tags.indexOf('os:web') >= 0) {
      actions.push({
        title: t('packages.play'),
        icon: <FaIcon solid play weak inline />,
        onAction: () => {
          navigate(linkStoreProjectBranchPackagePlay({
            projectId: props.project.id,
            branchId: props.branch.id,
            packageId: props.pkg.id,
          }));
        },
      });
    }

    actions.push({
      title: t('packages.download_zip'),
      icon: <FaIcon solid file-zipper weak inline />,
      onAction: () => showErrorScope(async () => {
        const url = new URL(await tokenlessApi.getStoreProjectsBranchesPackagesDownload({
          project: props.project.id,
          branch: props.branch.id,
          _package: props.pkg.id,
        }), config.storageBaseUrl);
        url.searchParams.set('filter', 'zip');
        const link = document.createElement('a');
        link.href = url.toString();
        link.download = `${props.project.title} ${props.pkg.title}.zip`;
        link.click();
      }),
    });

    if(config.supportTorrentDownloads) {
      actions.push({
        title: t('packages.download_torrent'),
        icon: <FaIcon solid magnet weak inline />,
        onAction: () => showErrorScope(async () => {
          const url = new URL(await tokenlessApi.getStoreProjectsBranchesPackagesTorrent({
            project: props.project.id,
            branch: props.branch.id,
            _package: props.pkg.id,
          }), config.storageBaseUrl);
          const link = document.createElement('a');
          link.href = url.toString();
          link.download = `${props.project.title} ${props.pkg.title}.torrent`;
          link.click();
        }),
      });
    }

    return actions;
  });

  return (
    <div>
      <Show when={tagsInfo().os}><FaIcon fa-2xl {...allTags.os.options[tagsInfo().os!].icon} /></Show>
      <Tags tags={ISet(props.pkg.tags)} showIcons={false} />
      <PrettyDataSize size={props.pkg.size} />
      <div class="buttons group">
        <ActionDropdown actions={actions()} />
      </div>
      <div class="title">{props.pkg.title}</div>
    </div>
  );
};
